import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { loadScript } from '@/helpers';
import { CustomerErrorCode } from '@/services/customer/customer.service';
import { Routes } from '@/router';
import { ChipotleUVerificationLinkClickEvent } from '@/services/analytics/events/chipotle-u-verification-link-click';

@Component({
    name: 'CmgIdMeVerificationModal'
})

export default class CmgIdMeVerificationModal extends Vue {
    isIdMeJSloaded = false;
    idMeClientId = this.$services.config.getIdMeClientId();
    idMeRedirectUrl = this.$services.config.getIdMeRedirectUrl();
    idMeWidgetScriptUrl = this.$services.config.getIdMeWidgetScriptUrl();

    @Watch('visible')
    onVisibleChange(newVal: boolean) {
        if (newVal) {
            this.tryBindIdMe();
            window.addEventListener('message', this.onIdMeCallback);
        } else {
            window.removeEventListener('message', this.onIdMeCallback);
        }
    }

    onIdMeCallback = (ev: Event): void => {
        this.listenForIdMeCallback(ev);
    }

    onWidgetClick(event: Event): void {
        const target = event.target as HTMLElement;
        const widgetButton = target.closest('#idme-verification .idme-trigger-link');

        if (widgetButton) {
            this.$services.analytics.send(new ChipotleUVerificationLinkClickEvent());
        }
    }

    private tryBindIdMe() {
        if (this.visible && this.isIdMeJSloaded) {
            this.$nextTick(() => {
                (window as any).BindIDme();
            })
        }
    }

    async mounted() {
        if (!this.isIdMeJSloaded) {
            await loadScript(this.idMeWidgetScriptUrl);
            this.isIdMeJSloaded = true;
            this.tryBindIdMe();
        }
    }

    private async listenForIdMeCallback(ev: Event) {
        const event = ev as MessageEvent;

        if (event && event.origin === new URL(this.idMeRedirectUrl).origin &&
            event.data && JSON.parse(event.data).type === 'IdMe' && JSON.parse(event.data).secCode === 'security') {
            try {
                const code = JSON.parse(event.data).code;
                if (code === null) {
                    this.$services.ui.setModalVisibility('idMeVerificationModal', false);
                    this.showWeHitASnagErrorModal();
                } else {
                    await this.$services.customer.updateStudentStatus(JSON.parse(event.data).code, true)
                        .then(() => {
                            this.$services.ui.setModalVisibility('idMeVerificationModal', false);
                            this.$services.ui.setModalVisibility('studentRegistrationSuccessModal', true);
                        })
                }
            } catch (error: any) {
                this.$services.logger.error('Failed to verify student status', undefined, error);
                this.$services.ui.setModalVisibility('idMeVerificationModal', false);
                if (error.errorCode === CustomerErrorCode.StudentIdAlreadyTaken) {
                    this.$services.ui.setModalVisibility('basicError', true, {
                        header: 'THIS STUDENT ID IS ALREADY TAKEN',
                        content: 'It looks like this student ID is already linked to an existing Chipotle account. Log in to the associated account to access Chipotle U Rewards.',
                        options: [
                            {
                                text: 'OKAY',
                                onClick: () => {
                                    this.$services.ui.setModalVisibility('basicError', false);
                                },
                            },
                        ],
                        isShort: true,
                        onClose: () => {
                            this.$services.ui.setModalVisibility('basicError', false);
                        }
                    });
                } else {
                    this.showWeHitASnagErrorModal();
                }
            }

        }
    }

    private showWeHitASnagErrorModal(){
        this.$services.ui.setModalVisibility('basicError', true, {
            header: 'WE HIT A SNAG',
            content: 'Sorry, something went wrong on our end. Please try again.',
            options: [
                {
                    text: 'OKAY',
                    onClick: () => {
                        this.$services.ui.setModalVisibility('basicError', false);
                        window.location.replace(`${window.location.origin}/chipotle-u-rewards`);  
                    },
                },
            ],
            isShort: true,
            onClose: () => {
                this.$services.ui.setModalVisibility('basicError', false);
            }
        });
    }

    get visible(): boolean {
        return this.$appStore.state.ui.modals.idMeVerificationModal.visible;
    }

    get isMobile(): boolean {
        return this.$services.ui.isMobile();
    }

    get backgroundImage(): string | undefined {
        return this.isMobile ?
            this.$appStore.state.cms.images.loyalty.idMeVerificationBackground.mobile
            : this.$appStore.state.cms.images.loyalty.idMeVerificationBackground.desktop
    }

    get chipotleULogo(): string {
        return this.$appStore.state.cms.images.loyalty.chipotleULogo.default;
    }

    get whatIsIdMeUrl(): string {
        return "https://www.id.me/individuals/group-discounts/students"
    }

    get rewardTermsUrl(): string {
        return "https://www.chipotle.com/rewards-terms"
    }

    onClose(): void {
        this.$services.ui.setModalVisibility('idMeVerificationModal', false);
        window.location.href = this.$router.resolve({ name: Routes.Rewards }).href;
    }
}